import { Link, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import './IndividualProgram.css';

const sortActivities = (activities) => {
  return [...activities].sort((a, b) => {
    const orderA = a.order !== undefined ? a.order : (a.activityOrder !== undefined ? a.activityOrder : null);
    const orderB = b.order !== undefined ? b.order : (b.activityOrder !== undefined ? b.activityOrder : null);

    if (orderA !== null && orderB !== null) return orderA - orderB;
    if (orderA !== null && orderB === null) return -1;
    if (orderA === null && orderB !== null) return 1;
    return a.name.localeCompare(b.name);
  });
};

const extractWeekNumber = (name = '') => {
  const match = String(name).match(/\d+/);
  return match ? match[0] : null;
};

const localizeScheduleLabel = (name = '') => (
  String(name)
    .replace(/\bWeek\b/gi, 'Uke')
    .replace(/\bDay\b/gi, 'Dag')
);

const getProgramLevelLabel = (program) => {
  if (program.level_name) {
    return /^\d+$/.test(String(program.level_name))
      ? `Nivå ${program.level_name}`
      : program.level_name;
  }
  if (program.level != null && program.level !== '') {
    return `Nivå ${program.level}`;
  }
  if (program.intensity_zone_idintensity_zone != null) {
    return `Nivå ${program.intensity_zone_idintensity_zone}`;
  }
  return null;
};

export default function IndividualProgram() {
  const { id } = useParams();
  const [program, setProgram] = useState(null);
  const [activities, setActivities] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [expandedDays, setExpandedDays] = useState({});

  useEffect(() => {
    fetchProgram();
  }, [id]);

  useEffect(() => {
    if (activities.length > 0 && Object.keys(expandedWeeks).length === 0) {
      setExpandedWeeks({ [activities[0].id]: true });
    }
  }, [activities, expandedWeeks]);

  const fetchProgram = async () => {
    try {
      setLoading(true);
      setExpandedWeeks({});
      setExpandedDays({});
      const [programResponse, activitiesResponse] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/programs/${id}`,
          { withCredentials: true }
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/programs/${id}/activities`,
          { withCredentials: true }
        )
      ]);

      setProgram(programResponse.data);
      setActivities(activitiesResponse.data.result || []);
      setError('');
    } catch (err) {
      console.error('Error fetching program:', err);
      setError('Kunne ikke laste program');
    } finally {
      setLoading(false);
    }
  };

  const toggleWeek = (weekId) => {
    setExpandedWeeks((prev) => ({
      ...prev,
      [weekId]: !prev[weekId]
    }));
  };

  const toggleDay = (weekId, dayId) => {
    const key = `${weekId}-${dayId}`;
    setExpandedDays((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (loading) {
    return <div className="prog-detail-loading">Laster program…</div>;
  }

  if (error) {
    return <div className="prog-detail-error">{error}</div>;
  }

  if (!program) {
    return <div className="prog-detail-error">Program ikke funnet</div>;
  }

  const heroImage = program.src || '/img/running.jpeg';
  const weekCount = Array.isArray(activities) ? activities.length : 0;
  const levelLabel = getProgramLevelLabel(program);
  const levelPillStyle =
    program.fargekode_bakgrunn && program.fargekode_tekst
      ? {
          backgroundColor: program.fargekode_bakgrunn,
          color: program.fargekode_tekst,
        }
      : undefined;

  return (
    <div className="prog-detail">
      <div className="prog-detail-container">
        <div className="prog-detail-top">
          <Link to="/programs" className="prog-detail-back">
            <i className="fas fa-arrow-left" aria-hidden="true" />
            Tilbake til alle programmer
          </Link>
        </div>

        <section className="prog-detail-hero">
          <div className="prog-detail-hero-media">
            <img
              src={heroImage}
              alt={program.name}
              onClick={() => setShowImageModal(true)}
            />
            <div className="prog-detail-hero-overlay" aria-hidden="true" />
            <div className="prog-detail-hero-content">
              <h1>{program.name}</h1>
              {program.short_description && (
                <p className="prog-detail-hero-lead">{program.short_description}</p>
              )}
              <div className="prog-detail-meta">
                {levelLabel && (
                  <span
                    className="prog-detail-pill prog-detail-pill--level"
                    style={levelPillStyle}
                  >
                    <i className="fas fa-chart-line" aria-hidden="true" />
                    {levelLabel}
                  </span>
                )}
                {weekCount > 0 && (
                  <span className="prog-detail-pill">
                    <i className="far fa-calendar-alt" aria-hidden="true" />
                    {weekCount} uker
                  </span>
                )}
                <span className={`prog-detail-pill ${program.isFree ? 'prog-detail-pill--accent' : ''}`}>
                  <i className={`fas ${program.isFree ? 'fa-gift' : 'fa-star'}`} aria-hidden="true" />
                  {program.isFree ? 'Gratis' : 'Premium'}
                </span>
                {program.businessName && (
                  <span className="prog-detail-pill">
                    <i className="fas fa-building" aria-hidden="true" />
                    {program.businessName}
                  </span>
                )}
              </div>
            </div>
          </div>
        </section>

        {(program.description || levelLabel) && (
          <section className="prog-detail-intro">
            <h2>Om programmet</h2>
            {levelLabel && (
              <p className="prog-detail-level-summary">
                <strong>Programnivå:</strong> {levelLabel}
                {program.level_description && (
                  <span> — {program.level_description}</span>
                )}
              </p>
            )}
            {program.description && <p>{program.description}</p>}
          </section>
        )}

        <section className="prog-detail-schedule">
          <div className="prog-detail-schedule-header">
            <h2>Programplan</h2>
            <p>Utvid ukene for å se dagene og øktene – steg for steg mot målet ditt.</p>
          </div>

          <div className="prog-detail-weeks">
            {Array.isArray(activities) && activities.map((week, weekIndex) => {
              const isWeekOpen = Boolean(expandedWeeks[week.id]);
              const weekNum = extractWeekNumber(week.name) || String(weekIndex + 1);

              return (
                <article
                  key={week.id}
                  className={`prog-detail-week ${isWeekOpen ? 'is-open' : ''}`}
                >
                  <button
                    type="button"
                    className="prog-detail-week-toggle"
                    onClick={() => toggleWeek(week.id)}
                    aria-expanded={isWeekOpen}
                  >
                    <span className="prog-detail-week-num">{weekNum}</span>
                    <div className="prog-detail-week-info">
                      <h3>{localizeScheduleLabel(week.name)}</h3>
                      {week.weeks_description && (
                        <p className="prog-detail-week-desc">{week.weeks_description}</p>
                      )}
                    </div>
                    <span className="prog-detail-week-chevron" aria-hidden="true">
                      <i className="fas fa-chevron-right" />
                    </span>
                  </button>

                  {isWeekOpen && (
                    <div className="prog-detail-week-body">
                      <div className="prog-detail-days">
                        {Array.isArray(week.days) && week.days.map((day) => {
                          const dayKey = `${week.id}-${day.id}`;
                          const isDayOpen = Boolean(expandedDays[dayKey]);
                          const dayActivities = Array.isArray(day.activities)
                            ? sortActivities(day.activities)
                            : [];

                          return (
                            <div key={day.id} className="prog-detail-day">
                              <button
                                type="button"
                                className="prog-detail-day-toggle"
                                onClick={() => toggleDay(week.id, day.id)}
                                aria-expanded={isDayOpen}
                              >
                                <h4>{localizeScheduleLabel(day.name)}</h4>
                                <i
                                  className={`fas fa-chevron-${isDayOpen ? 'down' : 'right'}`}
                                  aria-hidden="true"
                                />
                              </button>

                              {isDayOpen && (
                                <div className="prog-detail-day-body">
                                  {dayActivities.length > 0 ? (
                                    dayActivities.map((activity) => (
                                      <Link
                                        key={activity.id}
                                        to={`/activities/${activity.id}`}
                                        className="prog-detail-activity"
                                      >
                                        <h5>{activity.name}</h5>
                                        {activity.short_description && (
                                          <p className="prog-detail-activity-desc">
                                            {activity.short_description}
                                          </p>
                                        )}
                                        <div className="prog-detail-activity-tags">
                                          {activity.level_name && (
                                            <span className="prog-detail-tag">
                                              <i className="fas fa-chart-line" aria-hidden="true" />
                                              Nivå {activity.level_name}
                                            </span>
                                          )}
                                          {activity.step_name && (
                                            <span className="prog-detail-tag">
                                              <i className="fas fa-shoe-prints" aria-hidden="true" />
                                              Steg - {activity.step_name}
                                            </span>
                                          )}
                                          {activity.environment_name && (
                                            <span className="prog-detail-tag">
                                              <i className="fas fa-map-marker-alt" aria-hidden="true" />
                                              Miljø {activity.environment_name}
                                            </span>
                                          )}
                                          {activity.minutes && (
                                            <span className="prog-detail-tag">
                                              <i className="fas fa-clock" aria-hidden="true" />
                                              {activity.minutes} minutter   
                                            </span>
                                          )}
                                        </div>
                                      </Link>
                                    ))
                                  ) : (
                                    <p className="prog-detail-empty">Ingen aktiviteter planlagt</p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      </div>

      {showImageModal && (
        <div
          className="prog-detail-modal-overlay"
          onClick={() => setShowImageModal(false)}
          role="presentation"
        >
          <div
            className="prog-detail-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Programbilde"
          >
            <img src={heroImage} alt={program.name} />
            <button
              type="button"
              className="prog-detail-modal-close"
              onClick={() => setShowImageModal(false)}
              aria-label="Lukk"
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
